"use client"

import { authIdentity } from "@defuse-protocol/internal-utils"
import { useWatchHoldings } from "@src/components/DefuseSDK/features/account/hooks/useWatchHoldings"
import { createPrivateBalanceQueryOptions } from "@src/components/DefuseSDK/features/account/queries/privateBalanceQueries"
import type { Holding } from "@src/components/DefuseSDK/features/account/types/sharedTypes"
import { mapPrivateBalanceToHoldings } from "@src/components/DefuseSDK/features/account/utils/privateHoldingsMapper"
import { useTokensUsdPrices } from "@src/components/DefuseSDK/hooks/useTokensUsdPrices"
import type { TokenInfo } from "@src/components/DefuseSDK/types/base"
import { useConnectWallet } from "@src/hooks/useConnectWallet"
import { chainTypeToAuthMethod } from "@src/utils/chainTypeToAuthMethod"
import { useQuery } from "@tanstack/react-query"
import { useEffect, useMemo, useRef } from "react"

/**
 * Derives the NEAR Intents userId from the current wallet connection.
 * Returns null when the user is not authenticated.
 */
export function useIntentsUserId() {
  const { state } = useConnectWallet()

  return useMemo(() => {
    if (!state.isAuthorized || !state.address || !state.chainType) return null

    return authIdentity.authHandleToIntentsUserId(
      state.address,
      chainTypeToAuthMethod(state.chainType)
    )
  }, [state.isAuthorized, state.address, state.chainType])
}

export function useHoldingsAwareInitialToken({
  resolve,
  holdings,
  isSettled,
  readinessKey = "default",
  resolutionKey = readinessKey,
}: {
  resolve: (holdings: Holding[]) => TokenInfo | undefined
  holdings: Holding[] | undefined
  isSettled: boolean
  readinessKey?: string
  resolutionKey?: string
}) {
  const frozenTokenRef = useRef({
    resolutionKey,
    resolve,
    wasSettled: false,
    token: undefined as TokenInfo | undefined,
  })
  if (
    frozenTokenRef.current.resolutionKey !== resolutionKey ||
    frozenTokenRef.current.resolve !== resolve
  ) {
    frozenTokenRef.current = {
      resolutionKey,
      resolve,
      wasSettled: false,
      token: undefined,
    }
  }
  if (isSettled && !frozenTokenRef.current.wasSettled) {
    frozenTokenRef.current.token = resolve(holdings ?? [])
  }
  frozenTokenRef.current.wasSettled = isSettled
  const resolvedToken = isSettled ? frozenTokenRef.current.token : undefined

  const hasBeenReadyRef = useRef({
    readinessKey,
    value: false,
  })
  if (hasBeenReadyRef.current.readinessKey !== readinessKey) {
    hasBeenReadyRef.current = {
      readinessKey,
      value: false,
    }
  }

  useEffect(() => {
    if (isSettled) {
      hasBeenReadyRef.current = {
        readinessKey,
        value: true,
      }
    }
  }, [isSettled, readinessKey])

  return {
    resolvedToken,
    isReady: isSettled || hasBeenReadyRef.current.value,
    holdings: holdings ?? [],
  }
}

export function usePublicHoldingsAwareInitialToken({
  tokenList,
  resolve,
}: {
  tokenList: TokenInfo[]
  resolve: (holdings: Holding[]) => TokenInfo | undefined
}) {
  const source = usePublicHoldingsSource({ tokenList })

  return useHoldingsAwareInitialToken({
    ...source,
    resolve,
  })
}

export function usePrivateHoldingsAwareInitialToken({
  tokenList,
  resolve,
}: {
  tokenList: TokenInfo[]
  resolve: (holdings: Holding[]) => TokenInfo | undefined
}) {
  const source = usePrivateHoldingsSource({ tokenList })

  return useHoldingsAwareInitialToken({
    ...source,
    resolve,
  })
}

function usePublicHoldingsSource({ tokenList }: { tokenList: TokenInfo[] }) {
  const userId = useIntentsUserId()
  const holdingsQuery = useWatchHoldings({
    userId,
    tokenList,
  })

  return {
    holdings: holdingsQuery.data,
    isSettled:
      userId == null || holdingsQuery.data != null || holdingsQuery.isError,
    readinessKey: "public",
    resolutionKey: `public:${userId ?? "anonymous"}`,
  }
}

function usePrivateHoldingsSource({ tokenList }: { tokenList: TokenInfo[] }) {
  const userId = useIntentsUserId()
  const { data: usdPrices } = useTokensUsdPrices()
  const privateBalanceQuery = useQuery(
    createPrivateBalanceQueryOptions({ userId })
  )
  const holdings = useMemo(() => {
    if (
      privateBalanceQuery.isPending ||
      privateBalanceQuery.isError ||
      privateBalanceQuery.data == null
    ) {
      return undefined
    }
    return mapPrivateBalanceToHoldings(
      privateBalanceQuery.data,
      tokenList,
      usdPrices
    )
  }, [
    privateBalanceQuery.isPending,
    privateBalanceQuery.isError,
    privateBalanceQuery.data,
    tokenList,
    usdPrices,
  ])

  return {
    holdings,
    isSettled:
      userId == null || holdings != null || privateBalanceQuery.isError,
    readinessKey: "private",
    resolutionKey: `private:${userId ?? "anonymous"}`,
  }
}
